import { useContext, useEffect, useState } from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { ErrorMessages } from '@lib/helpers'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import { ShopFormShopStepValues } from '@lib/print/types'
import { StringsContext } from '@lib/strings-context'
import { UserContext } from '@lib/user/context'

import InputField from '@components/input-field/with-form-register'

interface ShopStepUserFormProps {
  register: UseFormRegister<ShopFormShopStepValues>
  signupErrorMessages: ErrorMessages
  errors: {
    firstName?: FieldError
    lastName?: FieldError
    email?: FieldError
    password?: FieldError
  }
}

const emailRegExp =
  /^[\p{L}a-zA-Z0-9_+&*-]+(?:\.[\p{L}a-zA-Z0-9_+&*-]+)*@(?:[\p{L}a-zA-Z0-9-]+\.)+[\p{L}a-zA-Z]{2,7}$/u

const ShopStepUserForm = ({
  register,
  signupErrorMessages,
  errors,
}: ShopStepUserFormProps) => {
  const strings = useContext(StringsContext)
  const { authStrings } = useContext(PrintShopFormStringsContext)

  const { user } = useContext(UserContext)

  const [isLoaded, setIsLoaded] = useState(false)

  const firstNameRegister = register('firstName', {
    validate: (firstName) => {
      if (!user?.isLoggedIn && !firstName) {
        return authStrings.signupFirstNameMissing
      }

      return true
    },
  })
  const lastNameRegister = register('lastName', {
    validate: (lastName) => {
      if (!user?.isLoggedIn && !lastName) {
        return authStrings.signupLastNameMissing
      }

      return true
    },
  })
  const emailRegister = register('email', {
    validate: (email) => {
      if (!user?.isLoggedIn && !email) {
        return authStrings.signupEmailMissing
      }

      if (!user?.isLoggedIn && !emailRegExp.test(email)) {
        return strings.emailInvalid
      }

      return true
    },
  })
  const passwordRegister = register('password', {
    validate: (password) => {
      if (!user?.isLoggedIn && !password) {
        return authStrings.signupPasswordMissing
      }

      return true
    },
  })

  // Wait for user to load
  useEffect(() => {
    if (!isLoaded && user) {
      setIsLoaded(true)
    }
  }, [isLoaded, user])

  if (!isLoaded || user?.isLoggedIn) {
    return null
  }

  return (
    <div className="space-y-4">
      <InputField
        type="text"
        formRegister={firstNameRegister}
        errorMessage={
          signupErrorMessages?.firstName ?? errors.firstName?.message
        }
        label={authStrings.signupFirstName}
        placeholder={authStrings.signupFirstNamePlaceholder}
        hasCustomInputStyles
        inputWrapperClassName="py-2 px-4 border border-gray-light rounded bg-white"
        inputClassName="text-base leading-snug bg-white"
        labelClassName="mb-2 font-semibold"
      />

      <InputField
        type="text"
        formRegister={lastNameRegister}
        errorMessage={signupErrorMessages?.lastName ?? errors.lastName?.message}
        label={authStrings.signupLastName}
        placeholder={authStrings.signupLastNamePlaceholder}
        hasCustomInputStyles
        inputWrapperClassName="py-2 px-4 border border-gray-light rounded bg-white"
        inputClassName="text-base leading-snug bg-white"
        labelClassName="mb-2 font-semibold"
      />

      <InputField
        type="email"
        autoComplete="email"
        formRegister={emailRegister}
        errorMessage={signupErrorMessages?.email ?? errors.email?.message}
        label={authStrings.signupEmail}
        placeholder={authStrings.signupEmailPlaceholder}
        hasCustomInputStyles
        inputWrapperClassName="py-2 px-4 border border-gray-light rounded bg-white"
        inputClassName="text-base leading-snug bg-white"
        labelClassName="mb-2 font-semibold"
      />

      <InputField
        type="password"
        autoComplete="off"
        formRegister={passwordRegister}
        errorMessage={signupErrorMessages?.password ?? errors.password?.message}
        label={authStrings.signupPassword}
        placeholder={authStrings.signupPasswordPlaceholder}
        hasCustomInputStyles
        inputWrapperClassName="py-2 px-4 border border-gray-light rounded bg-white"
        inputClassName="text-base leading-snug bg-white"
        labelClassName="mb-2 font-semibold"
      />
    </div>
  )
}

export default ShopStepUserForm
